import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Textarea,
} from '@mlm/ui';
import {
  createResource,
  createResourceCategory,
  deleteResource,
  deleteResourceCategory,
  listAdminResources,
  listResourceCategories,
  updateResource,
  uploadResourceFile,
  type AdminResource,
  type ResourceInput,
} from '../lib/api';
import { useHasPerm } from '../lib/auth-store';

const ACCEPT = '.pdf,.xlsx,.docx';

function fmtSize(bytes: number | null): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function ResourcesPage() {
  const qc = useQueryClient();
  const canWrite = useHasPerm('asset.write');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AdminResource | null>(null);
  const [newCategory, setNewCategory] = useState('');

  const catsQ = useQuery({
    queryKey: ['admin-resource-categories'],
    queryFn: listResourceCategories,
  });
  const rowsQ = useQuery({ queryKey: ['admin-resources'], queryFn: () => listAdminResources() });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin-resources'] });
    qc.invalidateQueries({ queryKey: ['admin-resource-categories'] });
  };

  const addCategory = useMutation({
    mutationFn: () => createResourceCategory({ name: newCategory.trim() }),
    meta: { successMessage: 'Category added.' },
    onSuccess: () => {
      setNewCategory('');
      invalidate();
    },
  });
  const removeCategory = useMutation({
    mutationFn: (id: string) => deleteResourceCategory(id),
    meta: { successMessage: 'Category removed.' },
    onSuccess: invalidate,
  });
  const removeResource = useMutation({
    mutationFn: (id: string) => deleteResource(id),
    meta: { successMessage: 'Resource removed.' },
    onSuccess: invalidate,
  });
  const toggleActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => updateResource(id, { active }),
    onSuccess: invalidate,
  });

  const categories = catsQ.data ?? [];
  const rows = rowsQ.data ?? [];
  const categoryName = (id: string | null) =>
    id ? (categories.find((c) => c.id === id)?.name ?? '—') : '—';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Resources</h1>
          <p className="text-sm text-muted-foreground">
            Documents and links members can open from their portal. PDF, Excel and Word are
            accepted; video goes in as a link.
          </p>
        </div>
        {canWrite ? (
          <Button
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
          >
            + Resource
          </Button>
        ) : null}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Categories</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {categories.length === 0 ? (
              <p className="text-sm text-muted-foreground">No categories yet.</p>
            ) : (
              categories.map((c) => (
                <span
                  key={c.id}
                  className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm"
                >
                  {c.name}
                  {canWrite ? (
                    <button
                      className="text-muted-foreground hover:text-destructive"
                      onClick={() => removeCategory.mutate(c.id)}
                      aria-label={`Remove ${c.name}`}
                    >
                      ✕
                    </button>
                  ) : null}
                </span>
              ))
            )}
          </div>
          {canWrite ? (
            <div className="flex gap-2">
              <Input
                className="h-8 max-w-xs"
                placeholder="New category"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
              />
              <Button
                size="sm"
                variant="outline"
                disabled={!newCategory.trim() || addCategory.isPending}
                onClick={() => addCategory.mutate()}
              >
                Add
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {rowsQ.isLoading ? (
            <p className="p-6 text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">No resources yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-4 py-2">Title</th>
                  <th className="px-4 py-2">Category</th>
                  <th className="px-4 py-2">Type</th>
                  <th className="px-4 py-2">Size</th>
                  <th className="px-4 py-2">Active</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b last:border-0">
                    <td className="px-4 py-2">
                      <button
                        className="text-left font-medium hover:underline"
                        onClick={() => {
                          setEditing(r);
                          setDialogOpen(true);
                        }}
                      >
                        {r.title}
                      </button>
                      {r.description ? (
                        <p className="text-xs text-muted-foreground">{r.description}</p>
                      ) : null}
                    </td>
                    <td className="px-4 py-2">{categoryName(r.categoryId)}</td>
                    <td className="px-4 py-2">
                      {r.kind === 'link' ? 'Link' : (r.originalFilename ?? 'File')}
                    </td>
                    <td className="px-4 py-2">{r.kind === 'link' ? '—' : fmtSize(r.sizeBytes)}</td>
                    <td className="px-4 py-2">
                      <Checkbox
                        checked={r.active}
                        disabled={!canWrite}
                        onCheckedChange={(v) =>
                          toggleActive.mutate({ id: r.id, active: v === true })
                        }
                      />
                    </td>
                    <td className="px-4 py-2 text-right">
                      {canWrite ? (
                        <button
                          className="text-muted-foreground hover:text-destructive"
                          onClick={() => removeResource.mutate(r.id)}
                          aria-label={`Delete ${r.title}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {dialogOpen ? (
        <ResourceDialog
          resource={editing}
          categories={categories.map((c) => ({ id: c.id, name: c.name }))}
          onClose={() => setDialogOpen(false)}
          onSaved={() => {
            setDialogOpen(false);
            invalidate();
          }}
        />
      ) : null}
    </div>
  );
}

function ResourceDialog({
  resource,
  categories,
  onClose,
  onSaved,
}: {
  resource: AdminResource | null;
  categories: { id: string; name: string }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(resource?.title ?? '');
  const [description, setDescription] = useState(resource?.description ?? '');
  const [categoryId, setCategoryId] = useState(resource?.categoryId ?? '');
  const [kind, setKind] = useState<'file' | 'link'>(resource?.kind ?? 'file');
  const [linkUrl, setLinkUrl] = useState(resource?.linkUrl ?? '');
  const [upload, setUpload] = useState<{
    key: string;
    originalFilename: string | null;
    mimeType: string;
    sizeBytes: number;
  } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // An edit keeps the existing file unless a new one is picked.
  const existingFile = resource?.kind === 'file' ? resource.originalFilename : null;
  const haveFile = !!upload || !!existingFile;

  async function onPick(file: File) {
    setUploading(true);
    setError(null);
    try {
      setUpload(await uploadResourceFile(file));
    } catch (err: unknown) {
      const code = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(
        code === 'unsupported_file_type'
          ? 'Only PDF, Excel (.xlsx) and Word (.docx) files are accepted. Older .xls/.doc files need to be re-saved in the newer format first.'
          : code === 'file_infected'
            ? 'That file failed the virus scan.'
            : 'Upload failed. Please try again.',
      );
    } finally {
      setUploading(false);
    }
  }

  const save = useMutation({
    mutationFn: async () => {
      const payload: ResourceInput = {
        title: title.trim(),
        description: description.trim() || null,
        categoryId: categoryId || null,
        kind,
        ...(kind === 'link'
          ? { linkUrl: linkUrl.trim(), fileKey: null }
          : upload
            ? {
                fileKey: upload.key,
                originalFilename: upload.originalFilename,
                mimeType: upload.mimeType,
                sizeBytes: upload.sizeBytes,
                linkUrl: null,
              }
            : {}),
      };
      return resource ? updateResource(resource.id, payload) : createResource(payload);
    },
    meta: { successMessage: resource ? 'Resource saved.' : 'Resource added.' },
    onSuccess: onSaved,
  });

  const ready = title.trim().length > 0 && (kind === 'link' ? linkUrl.trim().length > 0 : haveFile);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{resource ? 'Edit resource' : 'New resource'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <p className="mb-1 text-xs text-muted-foreground">Title</p>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div>
            <p className="mb-1 text-xs text-muted-foreground">Description (optional)</p>
            <Textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="mb-1 text-xs text-muted-foreground">Category</p>
              <select
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
              >
                <option value="">Uncategorized</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <p className="mb-1 text-xs text-muted-foreground">Type</p>
              <select
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                value={kind}
                onChange={(e) => setKind(e.target.value as 'file' | 'link')}
              >
                <option value="file">Document</option>
                <option value="link">Link (incl. video)</option>
              </select>
            </div>
          </div>

          {kind === 'link' ? (
            <div>
              <p className="mb-1 text-xs text-muted-foreground">URL</p>
              <Input
                placeholder="https://…"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
              />
              <p className="mt-1 text-[11px] text-muted-foreground">
                Videos go here — paste the YouTube / Vimeo / Drive link rather than uploading the
                file.
              </p>
            </div>
          ) : (
            <div>
              <p className="mb-1 text-xs text-muted-foreground">File</p>
              <input
                type="file"
                accept={ACCEPT}
                className="text-xs"
                disabled={uploading}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void onPick(f);
                }}
              />
              {uploading ? (
                <p className="mt-1 text-xs text-muted-foreground">Uploading…</p>
              ) : upload ? (
                <p className="mt-1 text-xs text-success">
                  {upload.originalFilename} · {fmtSize(upload.sizeBytes)}
                </p>
              ) : existingFile ? (
                <p className="mt-1 text-xs text-muted-foreground">
                  Current: {existingFile} — pick a file to replace it.
                </p>
              ) : null}
              <p className="mt-1 text-[11px] text-muted-foreground">
                PDF, Excel (.xlsx) or Word (.docx), up to 25 MB.
              </p>
            </div>
          )}

          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!ready || save.isPending || uploading} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
